import type Konva from "konva";
import { useCallback, useEffect, useState } from "react";

import { type SvgCallTooltip } from "../../ui-modules/genome-viewer/BlastHitTrack.js";
import { type CallTooltip, type TooltipState } from "../../ui-modules/genome-viewer/features-track/FeatureTrack.js";
import { type SetTooltipContent } from "../../ui-modules/genome-viewer/GenomeViewerTracksList.js";
import { type IFeatureInteraction } from "../track-shapes/track-shapes-konva-renderers.js";

const getKonvaShapePositionAndSize = (layer: Konva.Layer, targetShape: Konva.Rect) => {
  const { x, y } = layer.getStage().container().getBoundingClientRect();
  return {
    left: targetShape.x() + x,
    top: targetShape.y() + layer.y() + y, // layer is moved regarding margin and some text
    width: targetShape.width(),
    height: targetShape.height(),
  };
};

const useTrackTooltip = (layer: Konva.Layer, setTooltipContent?: SetTooltipContent) => {
  return useCallback<CallTooltip>(
    (shape, targetShape, tooltipState: Partial<TooltipState>) => {
      shape.on("mouseover", () => {
        setTooltipContent?.({
          style: getKonvaShapePositionAndSize(layer, targetShape),
          content: null, // erase, just in case
          ...tooltipState,
          shapeHovered: true,
        });
      });
      shape.on("mouseout", () => {
        setTooltipContent?.((previous) => ({
          ...previous,
          shapeHovered: false,
        }));
      });
      shape.on("click", () => {
        setTooltipContent?.((previous) => ({
          ...previous,
          shapeHovered: false,
        }));
      });
    },
    [layer, setTooltipContent],
  );
};

export const useSVGTrackTooltip = (setTooltipContent: SetTooltipContent) => {
  return useCallback<SvgCallTooltip>(
    (shape, targetShape, tooltipState: Partial<TooltipState>) => {
      shape.onmouseover = () => {
        const { left, top, width, height } = targetShape.getBoundingClientRect();
        setTooltipContent({
          style: { left, top, width, height },
          content: null,
          ...tooltipState,
          shapeHovered: true,
        });
      };

      shape.onmouseout = () => {
        setTooltipContent?.((previous) => ({
          ...previous,
          shapeHovered: false,
        }));
      };
    },
    [setTooltipContent],
  );
};

/*
the following would be very useful to control the interaction overlay from the outside
e.g. when hovering on a row of the features table
create a map for each created feature shape (featuresShapes.set(feature.feature.id, doInteract);)
and then store this map using  setFeatureShapesMap(featuresShapes); at the end of the refresh

  const [interactedFeatureId, setInteractedFeatureId] = useState<number>(null);
  const [featureShapesMap, setFeatureShapesMap] = useState<Map<number, IFeatureInteraction>>(null);
  useTrackInteraction(interactedFeatureId, featureShapesMap);

 */

const useTrackInteraction = (
  interactedFeatureId: number | null,
  featureShapesMap: Map<number, IFeatureInteraction> | null,
) => {
  const [currentInteraction, setCurrentInteraction] = useState<IFeatureInteraction>();
  useEffect(() => {
    currentInteraction?.hide();

    if (featureShapesMap && interactedFeatureId) {
      const doInteract = featureShapesMap.get(interactedFeatureId);
      setCurrentInteraction(doInteract);
      doInteract?.show();
    }
  }, [interactedFeatureId, featureShapesMap, currentInteraction]);
};

export { useTrackInteraction, useTrackTooltip };
